import React, { forwardRef,  useImperativeHandle, useRef, useState } from "react";
import { RCCAlert, RCCButton, RCCContainer, RCCGrid,  RCCHookToast, RCCHTMLTextEditor, RCCInput, RCCRefModalProps, RCCServicesApi,  RCCTooltip  } from "react-common-components";
import { useForm , useFieldArray} from "react-hook-form";
import Modal from "src/components/Modal";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import Tabs from "src/components/Tabs";
import Select from "src/components/Select";
import DateTimePickerRange from "src/components/DateTimePickerRange";
import ModalSide from "src/components/Modal/styles";
import { IRefITermsModals, ITerms, IComponentITermsModals } from "./interface";
import addAndChangeSchema  from "./schemas";

const SPLIT_SECTIONS = "<p class='section-of-use-terms'></p>";

const Modals: React.ForwardRefRenderFunction<IRefITermsModals, IComponentITermsModals> = ({
	paginationRef
}, ref) => {
	const { translate } = useTranslation();
	const {addToast} = RCCHookToast();
	const { confirm } = useConfirmModal();
	const [selectedTerm, setSelectedTerm] = useState<ITerms>({} as ITerms);
    
	// Modal Add/Change
	const [isAdd, setIsAdd] = useState(false);
	const modalAddOrChange = useRef<RCCRefModalProps>(null);
	const { handleSubmit : handleSubmitAddOrChange, register : registerAddOrChange, reset : resetAddOrChange, control : controlAddOrChange, setError : setErrorAddOrChange, getValues, formState: { errors : errorsAddOrChange }, clearErrors: clearErrorsAddOrChange } = useForm();
	
	const submitAddOrChange = async (data: any) => {
		try{
			clearErrorsAddOrChange();
			modalAddOrChange.current?.setLoadingButton();

			await addAndChangeSchema(translate).validate(data,{
				abortEarly: false
			});

			let termContent = "";

			if(data.tetba001 !== undefined){
				const arrayOfContents = data.tetba001.map((element: any) => element.tea001Conteudo);
				termContent = arrayOfContents.join(SPLIT_SECTIONS);
			}
			
			if(isAdd){
				await RCCServicesApi.postRequest({
					method: "TermsUse/Insert",
					body: {
						...data,
						tea001Conteudo: termContent
					}
				});
			}else{
				await RCCServicesApi.putRequest({
					method: "TermsUse/Update",
					body: {
						...data,
						tea001Id: selectedTerm.tea001Id,
						tea001Conteudo: termContent
					}
				});
			}

			addToast({
				color: "green",
				description: isAdd ? translate("Termo de uso adicionado com sucesso") : translate("Termo de uso adicionado alterado com sucesso"),
				title: translate("Sucesso")
			});

			modalAddOrChange.current?.resetLoadingButton();
			modalAddOrChange.current?.closeModal();
			paginationRef.current?.reload();

		}catch(err){
			modalAddOrChange.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorAddOrChange))
				return;

			standardErrorMessages(err, isAdd ? translate("Erro ao adicionar termo de uso") : translate("Erro ao alterar termo de uso"), addToast);
		}
	};

	const actionButtonAdd = () => {
		resetAddOrChange(); 
		setSelectedTerm({} as ITerms); 
		setIsAdd(true); 
		modalAddOrChange.current?.openModal();
	};
	const actionButtonChange = (row: ITerms) => {
		resetAddOrChange(); 
		setSelectedTerm(row);
		const sections = row.tea001Conteudo.split(SPLIT_SECTIONS);
		appendTerm(sections.map((section) => ({
			tea001Conteudo: section
		})));
		setIsAdd(false); 
		modalAddOrChange.current?.openModal(); 
	};


	// Delete 
	const actionButtonDelete = (row: ITerms) => {
		confirm(
			translate("Exclusão de termo de uso"),
			`${translate("Deseja realmente excluir o termo de uso")}: ${row.tea001Titulo}?`,
			async () => {
				await RCCServicesApi.deleteRequest({
					method: "TermsUse/Delete",
					urlParameters: {
						id: row.tea001Id
					}
				}); 
	
				addToast({
					color: "green",
					description: translate("Termo de uso excluído com sucesso"),
					title: translate("Sucesso")
				});

				paginationRef.current?.reload();
			},
			(err) => {
				standardErrorMessages(err, "Erro ao excluir termo de uso", addToast);
			},
			true
		);
	};

	useImperativeHandle(ref, () => ({
		actionButtonAdd,
		actionButtonChange,
		actionButtonDelete
	}));	

	const { 
		fields: fieldsTerms, 
		remove: removeTerm,
		append: appendTerm 
	} = useFieldArray({
		control: controlAddOrChange,
		name: "tetba001"
	});

	return (
		<>	
			{/* Modal ADD/CHANGE */}
			<Modal
				ref={modalAddOrChange}
				header={{
					title: isAdd ? translate("Cadastro de termos de aceitação") : translate("Alterar termos de aceitação"),
					icon: {
						name: "infoCircle"
					}
				}}
				onSubmit={handleSubmitAddOrChange(submitAddOrChange)}
				styleInline={ModalSide}
			>
				<Tabs 
					name="TabTerms"
					tabInit={0}
					options={[{
						label: translate("Seções"),
						content: (
							<>
								<RCCGrid container alignItems="flex-end" direction="column">
									
									<RCCTooltip
										text={translate("Nova seção")}
										color="purple"	
									>
										<RCCButton
											icon={{name: "plus", color: "#FFF", size: 18}}
											color="purple"
											layout="default"
											onClick={() => {appendTerm({tea001Conteudo: ""}); }}
											size="md"
										/>
									</RCCTooltip>
								</RCCGrid>	
								
								{fieldsTerms.length === 0 && (
									<RCCAlert 
										description={translate("Não há nenhuma seção para este termo de aceitação")}
									/>
								)}
								
								{fieldsTerms.map((field, index) => (
									<RCCContainer key={field.id}>
										<RCCHTMLTextEditor	
											styleInline="padding-top: 0px;"
											name={`tetba001.${index}.tea001Conteudo`}
											label={` ${translate("Seção")} ${index + 1}`}
											control={controlAddOrChange}	
											defaultValue={getValues(`tetba001.${index}.tea001Conteudo`)}	
										/>
										<RCCGrid container alignItems="flex-end" direction="column">
											<RCCTooltip
												text={translate("Excluir seção")}
												color="red"	
											>
												<RCCButton
													icon={{name: "trash", color: "#FFF", size: 12}}
													color="red"
													layout="default"
													onClick={() => { removeTerm(index); }}
													size="md"
												/>
											</RCCTooltip>
										</RCCGrid>	
									</RCCContainer>
								))}		
							</>	
						)},
					{
						label: translate("Propriedades"),
						content: (
							<>		
								<RCCGrid container style={{ marginBottom: "15px" }}>
									<RCCInput 
										register={registerAddOrChange}
										name="tea001Titulo"
										label={translate("Descrição")}
										maxLength={60}
										defaultValue={selectedTerm.tea001Titulo}
										error={errorsAddOrChange.tea001Titulo}
										required
									/>
								</RCCGrid>			
								<RCCGrid >
									<Select 
										control={controlAddOrChange}
										name="tea001Tipo" 
										label={translate("Tipo")}
										options={[
											{value: "1", label: translate("Termo de uso")},
											{value: "2", label: translate("Política de privacidade")}				
										]} 
										defaultValue={selectedTerm.tea001Tipo ? String(selectedTerm.tea001Tipo) : ""}
										submit="value"
										error={errorsAddOrChange.tea001Tipo}
										required
									/>
								</RCCGrid>
								<RCCGrid container>
									<DateTimePickerRange
										control={controlAddOrChange}
										nameInit = "tea001Dtinicio"
										nameEnd = "tea001Dtfim"
										label = {translate("Vigência")}
										labelSeparator = {translate("até")}
										type = "datePicker"
										defaultValueInit = {selectedTerm.tea001Dtinicio ? selectedTerm.tea001Dtinicio : null}
										defaultValueEnd = {selectedTerm.tea001Dtfim ? selectedTerm.tea001Dtfim : null}
										required
									/>
								</RCCGrid>				
							</>
						)
					}]} />
			</Modal>
		</>
	);
};

export default forwardRef(Modals);
